// Estudio de contenido con IA (migración 20261020a_content_studio.sql). Tipos de texto, estados y validaciones de la pantalla «Contenido».

export const KINDS = [
  { id: 'publicacion', label: 'Publicación', hint: 'Pie de foto para Instagram y Facebook' },
  { id: 'estado', label: 'Estado de WhatsApp', hint: 'Corto, para que lo vean tus contactos' },
  { id: 'anuncio', label: 'Anuncio', hint: 'Texto, título y descripción para Click-to-WhatsApp' },
  { id: 'reel', label: 'Reel', hint: '3 ganchos y guion con tiempos' },
  { id: 'plan', label: 'Plan de la semana', hint: '7 días de contenido' },
  { id: 'imagen', label: 'Imagen del anuncio (3 créditos)', hint: 'Foto publicitaria cuadrada generada con IA' }
];

// Lo que gasta cada tipo (igual que content_cost en la base).
export const KIND_COST = { imagen: 3 };
export const costOf = (kinds) => (kinds || []).reduce((sum, k) => sum + (KIND_COST[k] || 1), 0);
export const kindLabel = (id) => (id === 'imagen' ? 'Imagen' : id === 'perfil' || id === 'perfil_scan' ? 'Auditoría de perfil' : KINDS.find((k) => k.id === id)?.label || 'Texto');

export const MIN_SUBJECT = 3;
export const MAX_SUBJECT = 600;
export const MAX_BRIEF = 500;

// Los mismos límites que valida la base.
export const checkRequest = ({ kinds, subject, brief }, credits) => {
  if (!kinds || kinds.length === 0) return 'Elige qué textos quieres.';
  const s = String(subject || '').trim();
  if (s.length < MIN_SUBJECT) return 'Cuéntame de qué es el contenido (un producto, un servicio o un tema).';
  if (s.length > MAX_SUBJECT) return `El tema es muy largo (máximo ${MAX_SUBJECT} caracteres).`;
  if (String(brief || '').length > MAX_BRIEF) return `Las indicaciones son muy largas (máximo ${MAX_BRIEF} caracteres).`;
  const cost = costOf(kinds);
  if (credits && Number.isFinite(credits.restantes) && credits.restantes < cost) {
    return credits.restantes <= 0 ? 'No te quedan créditos de contenido este mes.' : `Te quedan ${credits.restantes} créditos y esto cuesta ${cost}.`;
  }
  return null;
};

const STATUS = {
  pendiente: { label: 'En cola…', tone: 'text-amber-300' },
  generando: { label: 'Redactando…', tone: 'text-amber-300' },
  listo: { label: 'Listo', tone: 'text-emerald-400' },
  error: { label: 'No se pudo redactar', tone: 'text-rose-300' },
  descartado: { label: 'Descartado', tone: 'text-seryi-text' },
  cancelado: { label: 'Cancelado', tone: 'text-seryi-text' }
};
export const contentStatus = (status) => STATUS[status] || { label: status || '—', tone: 'text-seryi-text' };
export const isWorking = (status) => status === 'pendiente' || status === 'generando';

// Tema que se propone al elegir algo del catálogo o de los servicios.
export const subjectFromItem = (item) => {
  if (!item) return '';
  const name = String(item.nombre || item.name || '').trim();
  const desc = String(item.descripcion || item.description || '').trim();
  const price = Number(item.precio ?? item.price);
  const parts = [name];
  if (desc) parts.push(desc);
  if (Number.isFinite(price) && price > 0) parts.push(`Precio: $${price.toLocaleString('es-CO')}`);
  return parts.filter(Boolean).join('. ').slice(0, MAX_SUBJECT);
};

// Resumen de los créditos del mes para la pantalla: cuánto queda, para qué alcanza y cuándo se renuevan (el 1 de cada mes, hora de Colombia).
export const creditsSummary = (credits, now = new Date()) => {
  if (!credits || !Number.isFinite(credits.limite)) return null;
  const limit = credits.limite;
  const used = Number.isFinite(credits.usados) ? credits.usados : 0;
  const left = Number.isFinite(credits.restantes) ? credits.restantes : Math.max(limit - used, 0);
  const bogota = new Date(now.getTime() - 5 * 3600 * 1000);
  const next = new Date(Date.UTC(bogota.getUTCFullYear(), bogota.getUTCMonth() + 1, 1));
  return {
    limit, used, left,
    percent: limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 100,
    images: Math.floor(left / KIND_COST.imagen),
    texts: left,
    renewal: next.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', timeZone: 'UTC' })
  };
};

// Auditoría de perfil de Instagram: la persona pega los datos de su perfil y se arma un solo tema para la IA (migración 20261023a_profile_audit.sql).
export const MAX_PROFILE_SUBJECT = 1800;
export const PROFILE_LIMITS = { usuario: 40, nombre: 80, bio: 300, enlace: 200, categoria: 60, destacados: 200, publicaciones: 700 };
export const EMPTY_PROFILE = { usuario: '', nombre: '', bio: '', enlace: '', categoria: '', destacados: '', publicaciones: '', foto: true, contacto: false, porSemana: '' };

export const buildProfileSubject = (p) => {
  const v = (k) => String(p?.[k] ?? '').trim().slice(0, PROFILE_LIMITS[k]);
  const semana = Number.parseInt(p?.porSemana, 10);
  return [
    `Usuario: ${v('usuario').replace(/^@+/, '') ? `@${v('usuario').replace(/^@+/, '')}` : '(sin dato)'}`,
    `Nombre del perfil: ${v('nombre') || '(sin dato)'}`,
    `Biografía: ${v('bio') || '(vacía)'}`,
    `Enlace: ${v('enlace') || '(sin enlace)'}`,
    `Categoría: ${v('categoria') || '(sin categoría)'}`,
    `Destacados: ${v('destacados') || '(ninguno)'}`,
    `Foto de perfil clara (logo o rostro): ${p?.foto ? 'sí' : 'no'}`,
    `Botón de contacto activado: ${p?.contacto ? 'sí' : 'no'}`,
    `Publicaciones por semana: ${Number.isFinite(semana) && semana >= 0 ? Math.min(semana, 21) : '(sin dato)'}`,
    `Textos de las últimas publicaciones: ${v('publicaciones') || '(no pegó ninguno)'}`
  ].join('\n').slice(0, MAX_PROFILE_SUBJECT);
};

export const checkProfile = (p, credits) => {
  if (String(p?.usuario || '').replace(/^@+/, '').trim().length < 2) return 'Escribe tu usuario de Instagram.';
  if (String(p?.bio || '').trim().length < 10) return 'Pega tu biografía (al menos 10 caracteres) para poder calificarla.';
  if (credits && Number.isFinite(credits.restantes) && credits.restantes < 1) return 'No te quedan créditos de contenido este mes.';
  return null;
};

// Escaneo de perfil: la persona escribe solo su usuario y n8n lee el perfil público (migración 20261024a_profile_scan.sql). Misma forma que valida la base.
export const normalizeInstagramUser = (value) => String(value || '').trim().replace(/^@+/, '');
export const checkInstagramUser = (value, credits) => {
  const u = normalizeInstagramUser(value);
  if (!/^[A-Za-z0-9._]{2,30}$/.test(u)) return 'Escribe tu usuario de Instagram, sin espacios (por ejemplo @minegocio).';
  if (credits && Number.isFinite(credits.restantes) && credits.restantes < 1) return 'No te quedan créditos de contenido este mes.';
  return null;
};
